import type { Section } from '../types';

/**
 * The power dashboard (Energia, ops) and the sunshine tile (guest).
 *
 * Ops Spanish is for the crew and the hosts: short lines, one instruction per
 * line, Panamanian words (la planta for the generator). The English beside it
 * is a real translation, for the owners.
 *
 * Nothing here ever states a number the screen does not have. Where a value is
 * missing the phrase says so and says why.
 */
export const power = {
  'ops.power.title': { es: 'Energía', en: 'Power' },
  'ops.power.nav_hint': {
    es: 'La batería, el sol y la planta.',
    en: 'The battery, the sun and the generator.',
  },

  // How old is what you are looking at
  'ops.power.updated_at': { es: 'Actualizado a las {time}', en: 'Updated at {time}' },
  'ops.power.age_now': { es: 'ahora mismo', en: 'just now' },
  'ops.power.age_min': { es: 'hace {count} min', en: '{count} min ago' },
  'ops.power.age_hours': { es: 'hace {count} h', en: '{count} h ago' },
  'ops.power.stale_since': {
    es: 'Sin datos nuevos desde las {time}',
    en: 'No new data since {time}',
  },
  'ops.power.silent': { es: 'Sin señal de la isla', en: 'No signal from the island' },
  'ops.power.silent_hint': {
    es: 'No te enseñamos un número viejo. Último dato: {time}',
    en: 'We do not show an old number. Last reading: {time}',
  },
  'ops.power.data_until': { es: 'Datos hasta las {time}', en: 'Data up to {time}' },
  'ops.power.cached': {
    es: 'Copia guardada en este teléfono, de las {time}',
    en: 'Copy saved on this phone, from {time}',
  },
  'ops.power.no_value': { es: 'Sin dato', en: 'No reading' },
  'ops.power.loading': { es: 'Buscando los datos...', en: 'Looking for the readings...' },
  'ops.power.nothing_yet': {
    es: 'Todavía no llegan datos de la isla.',
    en: 'No readings from the island yet.',
  },
  'ops.power.nothing_yet_hint': {
    es: 'Cuando la isla mande el primer dato, aparece aquí.',
    en: 'When the island sends its first reading, it shows up here.',
  },
  'ops.power.retry': { es: 'Buscar otra vez', en: 'Try again' },


  // The picture: where the power comes from and where it goes
  'ops.power.live_title': { es: 'Ahora mismo', en: 'Right now' },
  'ops.power.head.gen_charging': {
    es: 'La planta está prendida y carga la batería. Se apaga sola al llegar a {stop} por ciento.',
    en: 'The generator is running and charging the battery. It stops by itself at {stop} percent.',
  },
  'ops.power.head.gen_house': {
    es: 'La planta está prendida y le da la luz a la casa.',
    en: 'The generator is running and powering the house.',
  },
  'ops.power.head.battery_house': {
    es: 'La casa corre con la batería.',
    en: 'The house is running on the battery.',
  },
  'ops.power.head.sun_extra': {
    es: 'El sol alcanza para toda la casa y sobra para cargar la batería.',
    en: 'The sun covers the whole house and has extra to charge the battery.',
  },
  'ops.power.head.sun_plus_battery': {
    es: 'El sol y la batería mueven la casa.',
    en: 'The sun and the battery are running the house.',
  },
  'ops.power.head.sun_house': {
    es: 'El sol está dándole la luz a la casa.',
    en: 'The sun is feeding the house.',
  },
  'ops.power.head.none': {
    es: 'Sin datos frescos para decir de dónde viene la luz.',
    en: 'No fresh readings to say where the power is coming from.',
  },
  'ops.power.head.at_six': {
    es: 'A las 6 de la mañana quedará en {pct} por ciento.',
    en: 'At 6 in the morning it will be at {pct} percent.',
  },
  'ops.power.name.sun': { es: 'Sol', en: 'Sun' },
  'ops.power.name.house': { es: 'Casa', en: 'House' },
  'ops.power.name.battery': { es: 'Batería', en: 'Battery' },
  'ops.power.name.generator': { es: 'Planta', en: 'Generator' },
  'ops.power.flow.house_uses': { es: 'lo que gasta ahora', en: 'what it uses now' },
  'ops.power.flow.charging': { es: 'Cargando +{value}', en: 'Charging +{value}' },
  'ops.power.flow.draining': { es: 'Gastando -{value}', en: 'Draining -{value}' },
  'ops.power.flow.steady': { es: 'Sin cambio', en: 'Not changing' },
  'ops.power.flow.battery_unknown': { es: 'Sin dato de carga', en: 'No charge reading' },
  'ops.power.flow.lost': {
    es: 'Se pierde en la conversión: {value}',
    en: 'Lost in conversion: {value}',
  },
  'ops.power.flow.sun_helps': { es: 'El sol también aporta {value}.', en: 'The sun is also adding {value}.' },
  'ops.power.flow.battery_helps': {
    es: 'La batería también aporta {value} a la casa.',
    en: 'The battery is also giving {value} to the house.',
  },
  'ops.power.flow.gen_no_figure': { es: 'Sin dato de potencia', en: 'No power reading' },
  'ops.power.flow.night': { es: 'de noche', en: 'night' },
  'ops.power.flow.stop_line': {
    es: 'La raya marca {pct} por ciento, donde se apaga la planta.',
    en: 'The line marks {pct} percent, where the generator stops.',
  },
  'ops.power.flow.arrow_aria': { es: '{from} a {to}: {value}', en: '{from} to {to}: {value}' },

  // The air conditioners
  'ops.power.ac.title': { es: 'Aires', en: 'Air conditioners' },
  'ops.power.ac.count': { es: '{on} de {total} prendidos', en: '{on} of {total} on' },
  'ops.power.ac.count_partial': {
    es: '{on} prendidos, {missing} sin dato',
    en: '{on} on, {missing} with no reading',
  },
  'ops.power.ac.all_sleep': { es: 'Todos en modo dormir.', en: 'All in sleep mode.' },
  'ops.power.ac.hacienda': { es: 'Hacienda', en: 'Hacienda' },
  'ops.power.ac.bungalow': { es: 'Bungalow', en: 'Bungalow' },
  'ops.power.ac.cooling': { es: 'Frío', en: 'Cooling' },
  'ops.power.ac.sleep': { es: 'Dormir', en: 'Sleep' },
  'ops.power.ac.off': { es: 'Apagado', en: 'Off' },
  'ops.power.ac.unknown': { es: 'Sin dato', en: 'No reading' },
  'ops.power.ac.none': {
    es: 'Todavía no llegan los aires de la isla.',
    en: 'The air conditioners have not reported from the island yet.',
  },
  'ops.power.ac.legend': {
    es: 'Frío, Dormir y Apagado. Los vatios de cada aire necesitan un enchufe medidor, así que no se muestran.',
    en: 'Cooling, Sleep and Off. Watts per unit need a plug or clamp, so none are shown.',
  },

  // Today, tomorrow, the battery later
  'ops.power.today.title': { es: 'Hoy', en: 'Today' },
  'ops.power.today.sun_made': { es: 'El sol hizo', en: 'Sun made' },
  'ops.power.today.house_used': { es: 'La casa usó', en: 'House used' },
  'ops.power.today.tomorrow': { es: 'Mañana', en: 'Tomorrow' },
  'ops.power.today.tomorrow_value': { es: '{value} esperados', en: '{value} expected' },
  'ops.power.today.forecast_note': {
    es: 'Es un cálculo con el ángulo de los paneles supuesto, no medido.',
    en: 'An estimate built on an assumed panel angle, not a measured one.',
  },
  'ops.power.today.at_sunset': { es: 'Batería al irse el sol', en: 'Battery at sunset' },
  'ops.power.today.at_six': { es: 'Batería a las 6 a. m.', en: 'Battery at 6 AM' },
  'ops.power.today.no_value': { es: 'Sin dato', en: 'No reading' },

  // Water, entered by hand
  'ops.power.water.title': { es: 'Agua', en: 'Water' },
  'ops.power.water.hand': { es: 'Anotado a mano', en: 'Entered by hand' },
  'ops.power.water.never': {
    es: 'Nadie ha anotado el nivel todavía.',
    en: 'Nobody has entered a level yet.',
  },
  'ops.power.water.entered': { es: 'Anotado {when}', en: 'Entered {when}' },
  'ops.power.water.old': {
    es: 'Ya está viejo. Mira el medidor y anota otra vez.',
    en: 'This is old. Check the gauge and enter it again.',
  },
  'ops.power.water.pending': {
    es: 'Guardado en este teléfono, pendiente de enviar',
    en: 'Saved on this phone, waiting to send',
  },
  'ops.power.water.no_entry': { es: 'Sin anotar', en: 'Not entered' },
  'ops.power.water.low': { es: 'Baja', en: 'Low' },
  'ops.power.water.empty': { es: 'Vacío', en: 'Empty' },
  'ops.power.water.of_four': { es: '{q} de 4', en: '{q} of 4' },
  'ops.power.water.today_at': { es: 'hoy a las {time}', en: 'today at {time}' },
  'ops.power.water.yesterday_at': { es: 'ayer a las {time}', en: 'yesterday at {time}' },
  'ops.power.water.day_at': { es: 'el {date} a las {time}', en: '{date} at {time}' },
  'ops.power.water.enter': { es: 'Anotar niveles', en: 'Enter levels' },
  'ops.power.water.form_hint': {
    es: 'Mira cada medidor y toca su cuarto. Los tanques que no toques se quedan como están.',
    en: 'Read each sight gauge and tap its quarter. Tanks you do not tap stay as they are.',
  },
  'ops.power.water.save': { es: 'Guardar niveles', en: 'Save levels' },
  'ops.power.water.cancel': { es: 'Cancelar', en: 'Cancel' },
  'ops.power.water.saved': { es: 'Guardado.', en: 'Saved.' },
  'ops.power.water.save_none': { es: 'Toca al menos un tanque.', en: 'Tap at least one tank.' },
  'ops.power.water.pick_aria': { es: '{tank}: {q} de 4', en: '{tank}: {q} of 4' },
  'ops.power.water.tank.hacienda': { es: 'Hacienda', en: 'Hacienda' },
  'ops.power.water.tank.bungalow': { es: 'Bungalow', en: 'Bungalow' },
  'ops.power.water.tank.pavilion': { es: 'Pabellón', en: 'Pavilion' },
  'ops.power.water.tank.farm': { es: 'Tanques grandes', en: 'Tank farm' },
  'ops.power.water.tank.casita': { es: 'Casita', en: 'Casita' },

  // The island
  'ops.power.island.online': { es: 'La isla está en línea.', en: 'Island online.' },
  'ops.power.island.late': {
    es: 'La isla no ha mandado datos nuevos desde las {time}.',
    en: 'The island has sent nothing new since {time}.',
  },
  'ops.power.island.silent': { es: 'Sin señal de la isla.', en: 'No signal from the island.' },
  'ops.power.gen.stops_at': {
    es: 'Arranca sola a los {start} por ciento y se apaga a los {stop}.',
    en: 'Starts by itself at {start} percent and stops at {stop}.',
  },

  // The battery
  'ops.power.battery': { es: 'Batería', en: 'Battery' },
  'ops.power.battery_aria': { es: 'Batería al {pct} por ciento', en: 'Battery at {pct} percent' },
  'ops.power.trend.charging': { es: 'Subiendo', en: 'Charging' },
  'ops.power.trend.discharging': { es: 'Bajando', en: 'Draining' },
  'ops.power.trend.steady': { es: 'Estable', en: 'Steady' },
  'ops.power.trend_rate': { es: '{rate} por ciento por hora', en: '{rate} percent per hour' },
  'ops.power.stored': {
    es: '{kwh} kWh guardados, de {cap} kWh',
    en: '{kwh} kWh stored, of {cap} kWh',
  },
  'ops.power.refresh': { es: 'Actualizar', en: 'Refresh' },
  'ops.power.gen_starts_at': {
    es: 'La planta arranca sola a los {pct} por ciento.',
    en: 'The generator starts by itself at {pct} percent.',
  },

  // The battery later
  'ops.power.later': { es: 'La batería más tarde', en: 'The battery later' },
  'ops.power.at_sunset': { es: 'Cuando se va el sol ({time})', en: 'At sunset ({time})' },
  'ops.power.at_six': { es: 'A las 6 de la mañana', en: 'At 6 in the morning' },
  'ops.power.level.ok': { es: 'Bien', en: 'Fine' },
  'ops.power.level.low': { es: 'Baja', en: 'Low' },
  'ops.power.level.very_low': { es: 'Muy baja', en: 'Very low' },
  'ops.power.estimate': {
    es: 'Es un cálculo, no una promesa.',
    en: 'This is an estimate, not a promise.',
  },
  'ops.power.basis': {
    es: 'Cuenta con lo que gastó la casa en los últimos 30 minutos ({load}) y el sol que falta ({sun}).',
    en: 'Based on what the house used in the last 30 minutes ({load}) and the sun still to come ({sun}).',
  },
  'ops.power.basis_night_history': {
    es: 'De noche usa el gasto normal de las últimas noches ({load}).',
    en: 'At night it uses the normal use of recent nights ({load}).',
  },
  'ops.power.basis_night_recent': {
    es: 'De noche usa el mismo gasto de ahora.',
    en: 'At night it uses the same use as right now.',
  },
  'ops.power.gen_would_start': {
    es: 'La planta arrancaría como a las {time}',
    en: 'The generator would start around {time}',
  },
  'ops.power.later_old': {
    es: 'Este cálculo es de las {time} y ya está viejo',
    en: 'This estimate is from {time} and is out of date',
  },
  'ops.power.why.stale_battery': {
    es: 'No hay un dato nuevo de la batería.',
    en: 'There is no fresh battery reading.',
  },
  'ops.power.why.not_enough_recent_data': {
    es: 'Faltan datos de los últimos 30 minutos.',
    en: 'The last 30 minutes of readings are missing.',
  },
  'ops.power.why.no_forecast': {
    es: 'No hay pronóstico de sol ahora.',
    en: 'There is no solar forecast right now.',
  },

  // Right now, and today
  'ops.power.now_and_today': { es: 'Ahora y hoy', en: 'Now and today' },
  'ops.power.chart_scale': { es: 'Lo más alto de la gráfica: {value}', en: 'Top of the chart: {value}' },
  'ops.power.solar_now': { es: 'Sol ahora', en: 'Solar now' },
  'ops.power.house_now': { es: 'Casa ahora', en: 'House now' },
  'ops.power.solar_today': { es: 'Sol de hoy', en: 'Solar today' },
  'ops.power.house_today': { es: 'Gasto de hoy', en: 'Used today' },
  'ops.power.at_least': { es: 'Al menos {value}', en: 'At least {value}' },
  'ops.power.counted_by_app': { es: 'Contado por la app', en: 'Counted by the app' },
  'ops.power.from_inverter': { es: 'Del inversor', en: 'From the inverter' },

  // By the hour
  'ops.power.by_hour': { es: 'Gasto por hora, hoy', en: 'Use by hour, today' },
  'ops.power.by_hour_hint': {
    es: 'Toca una hora para ver el detalle.',
    en: 'Tap an hour for the detail.',
  },
  'ops.power.chart_label': {
    es: 'Gasto promedio de la casa en cada hora de hoy, en kilovatios.',
    en: 'Average house use in each hour of today, in kilowatts.',
  },
  'ops.power.hour_prev': { es: 'Hora anterior', en: 'Earlier hour' },
  'ops.power.hour_next': { es: 'Hora siguiente', en: 'Later hour' },
  'ops.power.day_old': { es: 'Estos datos no son de hoy.', en: 'This data is not from today.' },
  'ops.power.hour_at': { es: 'A las {time}', en: 'At {time}' },
  'ops.power.hour_house': { es: 'Casa: {value}', en: 'House: {value}' },
  'ops.power.hour_solar': { es: 'Sol: {value}', en: 'Solar: {value}' },
  'ops.power.hour_thin': {
    es: 'Faltan datos en esta hora. El número es parcial.',
    en: 'Some readings are missing in this hour. The number is partial.',
  },
  'ops.power.hour_none': { es: 'Sin datos en esta hora.', en: 'No readings in this hour.' },
  'ops.power.hour_load_line': { es: '{name}: {value}', en: '{name}: {value}' },

  // The biggest loads
  'ops.power.loads': { es: 'Lo que más gasta', en: 'Biggest loads' },
  'ops.power.loads_none': {
    es: 'Todavía no medimos cada aparato. Solo vemos el total de la casa.',
    en: 'We do not measure each appliance yet. Only the house total is shown.',
  },
  'ops.power.share': { es: '{pct} por ciento de la casa', en: '{pct} percent of the house' },
  'ops.power.ac_cooling': {
    es: 'Aires prendidos ahora: {cooling} de {total}',
    en: 'Air conditioners cooling now: {cooling} of {total}',
  },
  'ops.power.ac_cooling_only': {
    es: 'Aires prendidos ahora: {cooling}',
    en: 'Air conditioners cooling now: {cooling}',
  },
  'ops.power.ac_unmeasured': {
    es: 'No sabemos cuánto gasta cada aire.',
    en: 'We do not know how much each air conditioner uses.',
  },
  'ops.power.load.minisplits': { es: 'Aires', en: 'Air conditioners' },
  'ops.power.load.water_pump': { es: 'Bomba de agua', en: 'Water pump' },
  'ops.power.load.tank_pump': { es: 'Bomba del tanque grande', en: 'Tank farm pump' },
  'ops.power.load.pool_pump': { es: 'Bomba de la piscina', en: 'Pool pump' },
  'ops.power.load.hot_tub': { es: 'Jacuzzi', en: 'Hot tub' },
  'ops.power.load.laundry': { es: 'Lavandería', en: 'Laundry' },
  'ops.power.load.dryer': { es: 'Secadora', en: 'Dryer' },
  'ops.power.load.kitchen': { es: 'Cocina', en: 'Kitchen' },
  'ops.power.load.water_heaters': { es: 'Calentadores de agua', en: 'Water heaters' },

  // The generator
  'ops.power.generator': { es: 'La planta', en: 'The generator' },
  'ops.power.gen.running': { es: 'Funcionando desde las {time}', en: 'Running since {time}' },
  'ops.power.gen.running_now': { es: 'Funcionando', en: 'Running' },
  'ops.power.gen.off': { es: 'Apagada', en: 'Stopped' },
  'ops.power.gen.week': { es: 'Esta semana', en: 'This week' },
  'ops.power.gen.month': { es: 'Este mes', en: 'This month' },
  'ops.power.gen.hours': { es: '{hours} h prendida', en: '{hours} h running' },
  'ops.power.gen.starts_one': { es: '1 arranque', en: '1 start' },
  'ops.power.gen.starts': { es: '{count} arranques', en: '{count} starts' },
  'ops.power.gen.last_ran': { es: 'Última vez prendida', en: 'Last time it ran' },
  'ops.power.gen.today': { es: 'Hoy', en: 'Today' },
  'ops.power.gen.yesterday': { es: 'Ayer', en: 'Yesterday' },
  'ops.power.gen.days_ago': { es: 'Hace {count} días', en: '{count} days ago' },
  'ops.power.gen.running_when_seen': {
    es: 'Estaba prendida la última vez que se vio',
    en: 'It was running the last time it was seen',
  },
  'ops.power.gen.never_seen': {
    es: 'No la hemos visto prender desde el {date}',
    en: 'We have not seen it run since {date}',
  },
  'ops.power.gen.sunshine': {
    es: 'Días seguidos con puro sol: {count}',
    en: 'Days in a row on sunshine alone: {count}',
  },

  // The alert, on Hoy
  'ops.power.alert_title': { es: 'Batería baja y bajando', en: 'Battery low and falling' },
  'ops.power.alert_body': {
    es: 'Desde las {time}, mira el pedido y apaga lo que no hace falta',
    en: 'Since {time}, see the request and turn off what is not needed',
  },
  'ops.power.alert_see': { es: 'Ver la energía', en: 'See power' },

  // Guest: one quiet tile
  'guest.sunshine.name': { es: 'Con energía del sol', en: 'Running on sunshine' },
  'guest.sunshine.days_one': { es: '1 día seguido', en: '1 day straight' },
  'guest.sunshine.days': { es: '{count} días seguidos', en: '{count} days straight' },
  'guest.sunshine.hint': {
    es: 'La isla no ha necesitado el generador.',
    en: 'The island has not needed its generator.',
  },
} as const satisfies Section;
